import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import ImageUpload from './ImageUpload';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, 
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, Heading1, Heading2, Heading3,
  Undo, Redo, Quote, Code, Highlighter, Minus, Eye, Edit, 
  Save, X, FileText, Settings, Search, Upload
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 ${
      isActive ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <div className="w-px h-6 bg-gray-300 mx-1" />;

const MenuBar = ({ editor, onImageUploadClick }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-3 border-b bg-gray-50 sticky top-0 z-10">
      {/* Text Style */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
          <Heading1 size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 size={18} />
        </MenuButton>
      </div>
      
      <Divider />
      
      {/* Format */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold (Ctrl+B)">
          <Bold size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic (Ctrl+I)">
          <Italic size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline (Ctrl+U)">
          <UnderlineIcon size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
          <Strikethrough size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHighlight().run()} isActive={editor.isActive('highlight')} title="Highlight">
          <Highlighter size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Inline Code">
          <Code size={18} />
        </MenuButton>
      </div>
      
      <Divider />
      
      {/* Lists */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
          <List size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
          <ListOrdered size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
          <Quote size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
          <Minus size={18} />
        </MenuButton>
      </div>
      
      <Divider />
      
      {/* Alignment */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
          <AlignLeft size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
          <AlignCenter size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
          <AlignRight size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justify">
          <AlignJustify size={18} />
        </MenuButton>
      </div>
      
      <Divider />
      
      {/* Media */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Add Link">
          <LinkIcon size={18} />
        </MenuButton>
        <MenuButton onClick={onImageUploadClick} title="Insert Image">
          <ImageIcon size={18} />
        </MenuButton>
      </div>
      
      <Divider />
      
      {/* History */}
      <div className="flex gap-1 items-center">
        <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)">
          <Undo size={18} />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Y)">
          <Redo size={18} />
        </MenuButton>
      </div>
    </div>
  );
};

const BlogEditor = ({ post, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('edit');
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [imageUploadTarget, setImageUploadTarget] = useState(null); // 'content' or 'feature'
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    content: post?.content || '',
    excerpt: post?.excerpt || '',
    featureImage: post?.featureImage || '',
    author: post?.author || '',
    tags: post?.tags?.join(', ') || '',
    metaTitle: post?.metaTitle || '',
    metaDescription: post?.metaDescription || '',
    status: post?.status || 'draft',
    scheduledAt: post?.scheduledAt || ''
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      Highlight.configure({ multicolor: false }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: post ? prev.slug : generateSlug(title)
    }));
  };

  const wordCount = formData.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length;
  const charCount = formData.content.replace(/<[^>]*>/g, '').length;

  const handleSave = (status) => {
    // Validate scheduled posts have a date
    if (status === 'scheduled' && !formData.scheduledAt) {
      alert('Please select a date and time for scheduled publishing');
      return;
    }
    
    onSave({
      ...formData,
      status,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
      scheduledAt: status === 'scheduled' ? formData.scheduledAt : null
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-xl font-bold">{post ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button variant="outline" onClick={() => handleSave('draft')}>
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
          {formData.status === 'scheduled' && formData.scheduledAt && (
            <Button 
              onClick={() => handleSave('scheduled')} 
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Save className="w-4 h-4 mr-2" /> Schedule
            </Button>
          )}
          <Button onClick={() => handleSave('published')} className="bg-gradient-to-r from-cyan-500 to-blue-600">
            <FileText className="w-4 h-4 mr-2" /> Publish
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Main Editor Area */}
        <div className="flex-1 border-r">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4">
              <TabsList className="h-12">
                <TabsTrigger value="edit" className="flex items-center gap-2">
                  <Edit className="w-4 h-4" /> Edit
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Preview
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="edit" className="m-0">
              {/* Title */}
              <div className="p-4 border-b">
                <Input
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="Enter your blog post title..."
                  className="text-2xl font-bold border-0 shadow-none focus-visible:ring-0 px-0"
                />
              </div>

              {/* Editor */}
              <div className="border-b">
                <MenuBar 
                  editor={editor} 
                  onImageUploadClick={() => {
                    setImageUploadTarget('content');
                    setShowImageUpload(true);
                  }} 
                />
                <EditorContent 
                  editor={editor} 
                  className="prose max-w-none p-6 min-h-[400px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[380px]"
                />
              </div>

              {/* Word Count */}
              <div className="px-4 py-2 text-sm text-gray-500 flex gap-4 border-b">
                <span>{wordCount} words</span>
                <span>{charCount} characters</span>
              </div>
            </TabsContent>

            <TabsContent value="preview" className="m-0">
              <div className="p-8 max-w-3xl mx-auto">
                {formData.featureImage && (
                  <img src={formData.featureImage} alt="" className="w-full h-64 object-cover rounded-lg mb-6" />
                )}
                <h1 className="text-4xl font-bold mb-4">{formData.title || 'Untitled Post'}</h1>
                <div className="flex items-center gap-4 text-gray-500 mb-6">
                  <span>By {formData.author || 'Unknown Author'}</span>
                  <span>•</span>
                  <span>{new Date().toLocaleDateString()}</span>
                </div>
                <div 
                  className="prose prose-lg max-w-none
                    prose-headings:font-bold prose-headings:text-gray-900
                    prose-h1:text-4xl prose-h1:mb-6
                    prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                    prose-a:text-cyan-600 prose-a:no-underline hover:prose-a:underline
                    prose-strong:text-gray-900
                    prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                    prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                    prose-li:text-gray-700 prose-li:mb-2
                    prose-blockquote:border-l-4 prose-blockquote:border-cyan-500 prose-blockquote:bg-gray-50 prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:italic prose-blockquote:text-gray-600
                    prose-img:rounded-xl prose-img:shadow-lg"
                  dangerouslySetInnerHTML={{ __html: formData.content || '<p class="text-gray-400">Start writing to see preview...</p>' }}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="w-80 p-4 space-y-6 bg-gray-50">
          {/* Status */}
          <div>
            <Label className="text-sm font-semibold">Status</Label>
            <Select value={formData.status} onValueChange={(value) => {
              setFormData(prev => ({ 
                ...prev, 
                status: value,
                // Clear scheduledAt if not scheduling
                scheduledAt: value === 'scheduled' ? prev.scheduledAt : ''
              }));
            }}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Schedule Date/Time - Show only when status is 'scheduled' */}
          {formData.status === 'scheduled' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Label className="text-sm font-semibold text-amber-800">Schedule Publish</Label>
              <Input
                type="datetime-local"
                value={formData.scheduledAt ? formData.scheduledAt.slice(0, 16) : ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  scheduledAt: e.target.value ? new Date(e.target.value).toISOString() : ''
                }))}
                min={new Date().toISOString().slice(0, 16)}
                className="mt-2"
              />
              <p className="text-xs text-amber-700 mt-2">
                Post will be automatically published at this time
              </p>
            </div>
          )}

          {/* Slug */}
          <div>
            <Label className="text-sm font-semibold">URL Slug</Label>
            <Input
              value={formData.slug}
              onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              placeholder="post-url-slug"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">/blog/{formData.slug || 'your-post'}</p>
          </div>

          {/* Author */}
          <div>
            <Label className="text-sm font-semibold">Author</Label>
            <Input
              value={formData.author}
              onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
              placeholder="Author name"
              className="mt-1"
            />
          </div>

          {/* Feature Image */}
          <div>
            <Label className="text-sm font-semibold">Feature Image</Label>
            <div className="mt-1 space-y-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => {
                  setImageUploadTarget('feature');
                  setShowImageUpload(true);
                }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
              {formData.featureImage ? (
                <div className="relative group">
                  <img src={formData.featureImage} alt="" className="w-full h-32 object-cover rounded" />
                  <button 
                    onClick={() => setFormData(prev => ({ ...prev, featureImage: '' }))}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4 border-2 border-dashed rounded">No image selected</p>
              )}
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <Label className="text-sm font-semibold">Excerpt</Label>
            <Textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="Brief description for previews..."
              rows={3}
              className="mt-1"
            />
          </div>

          {/* Tags */}
          <div>
            <Label className="text-sm font-semibold">Tags</Label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="news, update, event"
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">Separate tags with commas</p>
          </div>

          {/* SEO Section */}
          <div className="pt-4 border-t">
            <h3 className="font-semibold flex items-center gap-2 mb-4">
              <Search className="w-4 h-4" /> SEO Settings
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Meta Title</Label>
                <Input
                  value={formData.metaTitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, metaTitle: e.target.value }))}
                  placeholder={formData.title || "Page title for search engines"}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{(formData.metaTitle || formData.title || '').length}/60 characters</p>
              </div>
              <div>
                <Label className="text-sm">Meta Description</Label>
                <Textarea
                  value={formData.metaDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, metaDescription: e.target.value }))}
                  placeholder="Description for search engines..."
                  rows={2}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">{(formData.metaDescription || '').length}/160 characters</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ProseMirror { outline: none; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 0.5rem; }
        .ProseMirror blockquote { border-left: 3px solid #e5e7eb; padding-left: 1rem; margin-left: 0; font-style: italic; color: #6b7280; }
        .ProseMirror code { background: #f3f4f6; padding: 0.2rem 0.4rem; border-radius: 0.25rem; font-size: 0.9em; }
        .ProseMirror mark { background-color: #fef08a; }
        .ProseMirror hr { border: none; border-top: 2px solid #e5e7eb; margin: 1.5rem 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; }
        .ProseMirror h1 { font-size: 2rem; font-weight: 700; margin: 1.5rem 0 0.75rem; }
        .ProseMirror h2 { font-size: 1.5rem; font-weight: 600; margin: 1.25rem 0 0.5rem; }
        .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; margin: 1rem 0 0.5rem; }
        .ProseMirror p { margin: 0.75rem 0; }
      `}</style>

      {/* Image Upload Dialog */}
      <ImageUpload
        open={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        onImageSelect={(url) => {
          if (imageUploadTarget === 'feature') {
            setFormData(prev => ({ ...prev, featureImage: url }));
          } else if (imageUploadTarget === 'content' && editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        }}
      />
    </div>
  );
};

export default BlogEditor;
